import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

type ModalOpenContextValue = {
  registerModal: () => void;
  unregisterModal: () => void;
  isModalOpen: boolean;
};

const noop = () => {};
const ModalOpenContext = createContext<ModalOpenContextValue>({
  registerModal: noop,
  unregisterModal: noop,
  isModalOpen: false,
});

export function ModalOpenProvider({ children }: { children: React.ReactNode }) {
  const [openCount, setOpenCount] = useState(0);

  const registerModal = useCallback(() => {
    setOpenCount((prev) => {
      return prev + 1;
    });
  }, []);

  const unregisterModal = useCallback(() => {
    setOpenCount((prev) => {
      return Math.max(0, prev - 1);
    });
  }, []);

  const value = useMemo(
    () => ({
      registerModal,
      unregisterModal,
      isModalOpen: openCount > 0,
    }),
    [registerModal, unregisterModal, openCount]
  );

  return <ModalOpenContext.Provider value={value}>{children}</ModalOpenContext.Provider>;
}

export function useModalOpenContext() {
  return useContext(ModalOpenContext);
}

export function useModalOpen(open: boolean) {
  const { registerModal, unregisterModal } = useModalOpenContext();
  const registeredRef = useRef(false);

  React.useEffect(() => {
    if (open && !registeredRef.current) {
      registerModal();
      registeredRef.current = true;
    }
    if (!open && registeredRef.current) {
      unregisterModal();
      registeredRef.current = false;
    }
    return () => {
      if (registeredRef.current) {
        unregisterModal();
        registeredRef.current = false;
      }
    };
  }, [open, registerModal, unregisterModal]);
}
